(() => {
  "use strict";

  const ANALYTICS_ENDPOINT = "https://lab.partforthat.com/api/analytics/browser-ingest";
  const LANDING_PATH = "/pages/water-filter-covers";
  const CAMPAIGN_KEYS = new Set([
    "dclid",
    "fbclid",
    "gad_campaignid",
    "gad_source",
    "gbraid",
    "gclid",
    "gclsrc",
    "li_fat_id",
    "msclkid",
    "ref",
    "ttclid",
    "twclid",
    "wbraid",
  ]);
  const LAB_CAMPAIGN_KEYS = new Set([
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "utm_id",
    "gclid",
    "gbraid",
    "wbraid",
    "fbclid",
    "msclkid",
    "ttclid",
    "li_fat_id",
    "twclid",
    "ref",
  ]);
  const EVENT_PROPERTIES = {
    page_view: new Set(["storefront_path"]),
    view_product: new Set(["storefront_path"]),
    product_card_click: new Set(["placement", "destination_path"]),
    model_selected: new Set(["selected_slug"]),
    model_not_found: new Set(["selection"]),
    add_to_cart_click: new Set(["placement", "destination_path"]),
    shop_all_click: new Set(["placement", "destination_path"]),
    contact_form_submit: new Set(),
  };
  const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
  const VISITOR_KEY = "pft_shop_solutions_visitor_v1";
  const VISIT_KEY = "pft_shop_solutions_visit_v1";
  const CONTACT_EVENT_KEY = "pft_shop_solutions_contact_event_v1";
  const VISITOR_LIFETIME = 365 * 24 * 60 * 60 * 1000;
  const VISIT_LIFETIME = 30 * 60 * 1000;
  const CONTACT_EVENT_LIFETIME = 30 * 60 * 1000;
  const MAX_RETRIES = 2;

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID().toLowerCase();
    }
    if (!window.crypto || typeof window.crypto.getRandomValues !== "function") {
      return "";
    }
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"))
      .join("")
      .replace(/^(.{8})(.{4})(.{4})(.{4})(.{12})$/, "$1-$2-$3-$4-$5");
  }

  function isCampaignKey(key) {
    return CAMPAIGN_KEYS.has(key) || /^utm_[a-z0-9_]{1,64}$/.test(key);
  }

  function campaignParameters(search) {
    const output = new URLSearchParams();
    let count = 0;
    let totalLength = 0;

    new URLSearchParams(search).forEach((rawValue, key) => {
      if (!isCampaignKey(key) || output.has(key) || count >= 20) return;
      const value = rawValue.slice(0, 512);
      if (totalLength + key.length + value.length > 4096) return;
      output.set(key, value);
      totalLength += key.length + value.length;
      count += 1;
    });
    return output;
  }

  function campaignObject(search) {
    const campaign = {};
    let totalLength = 0;

    new URLSearchParams(search).forEach((rawValue, key) => {
      if (!LAB_CAMPAIGN_KEYS.has(key) || Object.prototype.hasOwnProperty.call(campaign, key)) return;
      if (Object.keys(campaign).length >= 15) return;
      const value = rawValue.slice(0, 512);
      if (!value) return;
      if (totalLength + key.length + value.length > 4096) return;
      campaign[key] = value;
      totalLength += key.length + value.length;
    });
    return campaign;
  }

  function clearAnalyticsIdentifiers() {
    try {
      window.localStorage.removeItem(VISITOR_KEY);
    } catch (_) {
      // Storage may be unavailable.
    }
    try {
      window.sessionStorage.removeItem(VISIT_KEY);
      window.sessionStorage.removeItem(CONTACT_EVENT_KEY);
    } catch (_) {
      // Storage may be unavailable.
    }
  }

  function getVisitor() {
    const now = Date.now();
    try {
      const stored = JSON.parse(window.localStorage.getItem(VISITOR_KEY) || "null");
      if (stored && UUID_PATTERN.test(stored.id) && stored.expiresAt > now) {
        return { id: stored.id };
      }
      const id = uuid();
      if (!id) return null;
      window.localStorage.setItem(VISITOR_KEY, JSON.stringify({
        id,
        createdAt: now,
        expiresAt: now + VISITOR_LIFETIME,
      }));
      return { id };
    } catch (_) {
      return null;
    }
  }

  function getVisit() {
    const now = Date.now();
    const campaign = campaignObject(window.location.search);
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(VISIT_KEY) || "null");
      const campaignChanged = stored && JSON.stringify(stored.campaign || {}) !== JSON.stringify(campaign);
      if (
        stored &&
        UUID_PATTERN.test(stored.id) &&
        stored.landingPath === LANDING_PATH &&
        now - stored.lastSeenAt < VISIT_LIFETIME &&
        !campaignChanged
      ) {
        stored.lastSeenAt = now;
        window.sessionStorage.setItem(VISIT_KEY, JSON.stringify(stored));
        return stored;
      }
    } catch (_) {
      // A new in-memory visit below is still usable when storage is blocked.
    }

    const visit = {
      id: uuid(),
      startedAt: new Date(now).toISOString(),
      landingPath: LANDING_PATH,
      campaign,
      lastSeenAt: now,
    };
    try {
      window.sessionStorage.setItem(VISIT_KEY, JSON.stringify(visit));
    } catch (_) {
      // The in-memory visit remains valid for this page.
    }
    return visit;
  }

  function productFromElement(element) {
    if (!element) return null;
    const data = element.dataset;
    if (!data.productHandle || !data.productModel) return null;
    const product = {
      slug: data.productHandle,
      model: data.productModel,
      currency: "USD",
    };
    if (data.productSku) product.sku = data.productSku;
    if (data.productVariant) product.variant_id = data.productVariant;
    const price = Number(data.productPrice);
    if (Number.isFinite(price) && price >= 0) product.price = price;
    return product;
  }

  function referrerPayload() {
    try {
      const referrer = new URL(document.referrer);
      return {
        host: referrer.hostname.slice(0, 253),
        path: referrer.pathname.slice(0, 1024),
      };
    } catch (_) {
      return null;
    }
  }

  function clientPayload() {
    let timezone = "";
    try {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch (_) {
      // Timezone is optional.
    }
    return {
      language: (navigator.language || "").slice(0, 64),
      timezone: timezone.slice(0, 100),
      viewport: {
        width: Math.max(0, Math.round(window.innerWidth)),
        height: Math.max(0, Math.round(window.innerHeight)),
      },
    };
  }

  function analyticsConsentState() {
    if (navigator.globalPrivacyControl === true) return false;
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== "function") return null;
    try {
      return privacy.analyticsProcessingAllowed() === true;
    } catch (_) {
      return null;
    }
  }

  function loadCustomerPrivacy(callback) {
    if (window.Shopify && window.Shopify.customerPrivacy) {
      callback();
      return;
    }
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== "function") {
      callback();
      return;
    }
    try {
      window.Shopify.loadFeatures(
        [{ name: "consent-tracking-api", version: "0.1" }],
        () => callback(),
      );
    } catch (_) {
      callback();
    }
  }

  function sanitizeProperties(eventName, properties) {
    const allowed = EVENT_PROPERTIES[eventName];
    if (!allowed) return null;
    const sanitized = {};
    Object.entries(properties || {}).forEach(([key, rawValue]) => {
      if (!allowed.has(key) || typeof rawValue !== "string") return;
      const value = rawValue.slice(0, 1024);
      if (key === "storefront_path" && value !== LANDING_PATH) return;
      if (key === "destination_path" && !/^\/products\/[a-z0-9][a-z0-9-]*\/?$/.test(value)) return;
      if (key === "selected_slug" && !/^[a-z0-9][a-z0-9-]*$/.test(value)) return;
      sanitized[key] = value;
    });
    return sanitized;
  }

  function getPendingContactEventId() {
    try {
      const pending = JSON.parse(window.sessionStorage.getItem(CONTACT_EVENT_KEY) || "null");
      if (
        pending &&
        UUID_PATTERN.test(pending.id) &&
        Date.now() - pending.createdAt < CONTACT_EVENT_LIFETIME
      ) {
        return pending.id;
      }
    } catch (_) {
      // A fresh event ID below remains usable when storage is blocked.
    }
    return "";
  }

  function setPendingContactEventId(id) {
    try {
      window.sessionStorage.setItem(CONTACT_EVENT_KEY, JSON.stringify({ id, createdAt: Date.now() }));
    } catch (_) {
      // The in-memory request remains usable when storage is blocked.
    }
  }

  function clearPendingContactEvent() {
    try {
      window.sessionStorage.removeItem(CONTACT_EVENT_KEY);
    } catch (_) {
      // Storage may be unavailable.
    }
  }

  function clearContactSuccessFromUrl() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("contact_posted");
      window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    } catch (_) {
      // URL cleanup is optional; server deduplication still protects the event.
    }
  }

  function retryDelay(response, attempt) {
    const retryAfter = Number(response.headers.get("Retry-After"));
    if (Number.isFinite(retryAfter) && retryAfter > 0) {
      return Math.min(retryAfter * 1000, 5 * 60 * 1000);
    }
    return Math.min(30000 * (2 ** attempt) + Math.round(Math.random() * 1000), 5 * 60 * 1000);
  }

  function createTracker(root) {
    if (window.Shopify && window.Shopify.designMode) {
      return { track: () => false, onStateChange: (handler) => handler(false), getVisitId: () => "" };
    }
    // Share site-wide identity and attribution when the new collector is enabled.
    // The existing collector remains the fallback until Lab v2 is ready.
    if (window.PartForThatAnalytics && window.PartForThatAnalytics.sitewide === true) {
      return window.PartForThatAnalytics;
    }
    const environment = root.dataset.analyticsEnvironment === "production" ? "production" : "test";
    const stateHandlers = [];
    const retryTimers = new Set();
    const activeRequests = new Set();
    let stateKnown = false;
    let active = false;
    let visitor = null;
    let visit = null;

    function cancelRetries() {
      retryTimers.forEach((timer) => window.clearTimeout(timer));
      retryTimers.clear();
      activeRequests.forEach((controller) => controller.abort());
      activeRequests.clear();
    }

    function notifyState() {
      stateHandlers.forEach((handler) => handler(active));
    }

    function evaluateConsent() {
      const consent = analyticsConsentState();
      if (consent === null) return;
      if (stateKnown && consent === active) return;
      stateKnown = true;
      active = consent;
      if (active) {
        visitor = getVisitor();
        visit = getVisit();
      } else {
        cancelRetries();
        visitor = null;
        visit = null;
        clearAnalyticsIdentifiers();
      }
      notifyState();
    }

    function postPayload(body, attempt, onSuccess) {
      if (!active || analyticsConsentState() !== true) return;
      const controller = new AbortController();
      activeRequests.add(controller);
      fetch(ANALYTICS_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        credentials: "omit",
        keepalive: true,
        signal: controller.signal,
      }).then((response) => {
        activeRequests.delete(controller);
        if (response.status === 204) {
          if (typeof onSuccess === "function") onSuccess();
          return;
        }
        if (response.status !== 429 || attempt >= MAX_RETRIES) return;
        const timer = window.setTimeout(() => {
          retryTimers.delete(timer);
          postPayload(body, attempt + 1, onSuccess);
        }, retryDelay(response, attempt));
        retryTimers.add(timer);
      }).catch(() => {
        activeRequests.delete(controller);
        // Network and non-429 failures are deliberately not retried.
      });
    }

    function track(eventName, options = {}) {
      if (!active || analyticsConsentState() !== true || !visit || !visit.id || !EVENT_PROPERTIES[eventName]) {
        return false;
      }
      const eventId = UUID_PATTERN.test(options.eventId || "") ? options.eventId : uuid();
      const properties = sanitizeProperties(eventName, options.properties);
      if (!eventId || properties === null) return false;

      const isContactEvent = eventName === "contact_form_submit";
      const referrer = isContactEvent ? null : referrerPayload();
      const payload = {
        schema_version: 1,
        environment,
        event_id: eventId,
        event_name: eventName,
        occurred_at: new Date().toISOString(),
        ...(visitor ? { visitor } : {}),
        visit: {
          id: visit.id,
          started_at: visit.startedAt,
          landing_path: visit.landingPath,
        },
        page: {
          path: LANDING_PATH,
          title: isContactEvent ? "Water Filter Covers" : document.title.slice(0, 300),
          type: "hub",
        },
        ...(!isContactEvent && referrer ? { referrer } : {}),
        ...(!isContactEvent && Object.keys(visit.campaign).length ? { campaign: visit.campaign } : {}),
        ...(!isContactEvent ? { client: clientPayload() } : {}),
        ...(!isContactEvent ? { product: options.product || null } : {}),
        properties,
      };
      const body = JSON.stringify(payload);
      if (new Blob([body]).size > 16 * 1024) return false;

      window.dispatchEvent(new CustomEvent("pft:solutions-event", {
        detail: { eventName, ...options },
      }));
      postPayload(body, 0, options.onSuccess);
      return true;
    }

    function onStateChange(handler) {
      stateHandlers.push(handler);
      if (stateKnown) handler(active);
    }

    document.addEventListener("visitorConsentCollected", evaluateConsent);
    window.addEventListener("pageshow", evaluateConsent);
    evaluateConsent();
    loadCustomerPrivacy(evaluateConsent);

    return {
      track,
      onStateChange,
      getVisitId: () => (visit && visit.id) || "",
    };
  }

  function contactSubmissionSucceeded(root) {
    return (
      new URLSearchParams(window.location.search).get("contact_posted") === "true" &&
      Boolean(root.querySelector("[data-pfts-contact-success]"))
    );
  }

  function init(root) {
    if (!root || root.dataset.pftsInitialized === "true") return;
    root.dataset.pftsInitialized = "true";

    const tracker = createTracker(root);

    root.querySelectorAll("[data-pfts-product-link]").forEach((link) => {
      link.addEventListener("click", () => {
        const card = link.closest(".pfts-product-card");
        const source = link.dataset.productHandle
          ? link
          : card && card.querySelector("[data-product-handle]");
        tracker.track("product_card_click", {
          product: productFromElement(source),
          properties: {
            placement: "hub_product_card",
            destination_path: new URL(link.href).pathname,
          },
        });
      });
    });

    const modelFinder = root.querySelector("[data-pfts-model-finder]");
    if (modelFinder) {
      modelFinder.addEventListener("submit", (event) => {
        event.preventDefault();
        const select = modelFinder.querySelector("select");
        const selected = select && select.options[select.selectedIndex];
        if (!selected || !selected.value) {
          if (select) select.reportValidity();
          return;
        }
        if (selected.value === "other") {
          tracker.track("model_not_found", { properties: { selection: "other" } });
          const help = root.querySelector(".pfts-model-help");
          if (help) {
            help.scrollIntoView({ behavior: "smooth", block: "center" });
            window.setTimeout(() => help.focus({ preventScroll: true }), 450);
          }
          return;
        }
        const destination = new URL(selected.value, window.location.origin);
        tracker.track("model_selected", {
          properties: { selected_slug: destination.pathname.split("/").filter(Boolean).pop() || "" },
        });
        window.location.assign(destination.toString());
      });
    }

    const cartForms = Array.from(root.querySelectorAll(".pfts-cart-form"));
    cartForms.forEach((form) => {
      const returnTo = form.querySelector("[data-pfts-return-to]");
      const campaignInput = form.querySelector("[data-pfts-campaign]");
      const landingInput = form.querySelector("[data-pfts-landing]");
      const visitInput = form.querySelector("[data-pfts-visit-id]");
      const cartUrl = new URL("/cart", window.location.origin);
      if (returnTo) returnTo.value = cartUrl.pathname;
      if (campaignInput) campaignInput.value = "{}";
      if (landingInput) landingInput.value = window.location.pathname;
      if (visitInput) visitInput.value = "unavailable";

      form.addEventListener("submit", () => {
        const button = form.querySelector("[data-pfts-add-to-cart]");
        const product = productFromElement(button);
        tracker.track("add_to_cart_click", {
          product,
          properties: {
            placement: "hub_product_card",
            destination_path: product ? `/products/${product.slug}` : "",
          },
        });
        if (tracker.sitewide) {
          // Tracking can renew an expired visit or detect withdrawn consent.
          // Submit the current attribution, not the values from initial page load.
          const currentVisitId = tracker.getVisitId();
          if (campaignInput) campaignInput.value = currentVisitId ? JSON.stringify(tracker.getCampaign()) : "{}";
          if (visitInput) visitInput.value = currentVisitId || "unavailable";
          if (landingInput) landingInput.value = currentVisitId ? tracker.getLandingPath() || LANDING_PATH : LANDING_PATH;
        }
      });
    });

    root.querySelectorAll("[data-pfts-shop-all]").forEach((link) => {
      link.addEventListener("click", () => {
        tracker.track("shop_all_click", {
          properties: {
            placement: link.dataset.pftsPlacement || "landing_page",
            destination_path: new URL(link.href).pathname,
          },
        });
      });
    });

    tracker.onStateChange((allowed) => {
      cartForms.forEach((form) => {
        const campaignInput = form.querySelector("[data-pfts-campaign]");
        const landingInput = form.querySelector("[data-pfts-landing]");
        const visitInput = form.querySelector("[data-pfts-visit-id]");
        if (campaignInput) {
          campaignInput.value = allowed
            ? JSON.stringify(tracker.sitewide ? tracker.getCampaign() : Object.fromEntries(campaignParameters(window.location.search).entries()))
            : "{}";
        }
        if (landingInput && tracker.sitewide) {
          landingInput.value = allowed ? tracker.getLandingPath() || LANDING_PATH : LANDING_PATH;
        }
        if (visitInput) visitInput.value = allowed ? tracker.getVisitId() || "unavailable" : "unavailable";
      });

      if (!allowed) return;
      if (!tracker.sitewide) {
        tracker.track("page_view", { properties: { storefront_path: LANDING_PATH } });
      }
      if (contactSubmissionSucceeded(root)) {
        const eventId = getPendingContactEventId() || uuid();
        if (eventId) {
          setPendingContactEventId(eventId);
          tracker.track("contact_form_submit", {
            eventId,
            properties: {},
            onSuccess: () => {
              clearPendingContactEvent();
              clearContactSuccessFromUrl();
            },
          });
        }
      }
    });
  }

  function initAll(container = document) {
    if (container.matches && container.matches("[data-pfts-root]")) init(container);
    container.querySelectorAll("[data-pfts-root]").forEach(init);
  }

  initAll();
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
